import { Link, useLocation } from "react-router"
import { CalendarBlank, ClockCounterClockwise, List, User, UserGear, WarningCircle } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { ReleaseInfo } from "@/layouts/release-info"
import { UserPopup } from "@/layouts/user-popup"
import { cn } from "@/lib/utils"
import { getChatRoute, useIsChatLocation } from "@/modules/chats/lib/routing-helpers"
import { useOptionalChatThreadContext } from "@/modules/chats/stores/useChatThreadStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

export const PATIENT_ROUTE = {
  AGENDA: "agenda",
  INFORMATION: "information",
  HISTORY: "history",
}

type TabButtonProps = {
  id: string
  title: string
  icon: React.JSX.Element
  isActive?: boolean
}

const TabButton = ({ id, title, icon, isActive }: TabButtonProps) => {
  return (
    <Button
      key={id}
      title={title}
      data-testid={id}
      variant="circle"
      className={cn(
        "hover:bg-surface-hovered relative h-14 w-14",
        isActive && "bg-primary-surface hover:bg-primary-surface-hovered",
      )}>
      <span className={cn("text-on-surface-subdued", isActive && "text-on-primary-surface")}>{icon}</span>
    </Button>
  )
}

const UserWarningIcon = () => {
  return (
    <>
      <User className="relative" />
      <WarningCircle weight="fill" className="text-critical absolute -mt-6 ml-2.5 h-5 w-5" />
    </>
  )
}

const CalendarBlankWarningIcon = () => {
  return (
    <>
      <CalendarBlank />
      <WarningCircle weight="fill" className="text-critical absolute top-4 right-2 h-5 w-5" />
    </>
  )
}

interface PatientNavbarProps {
  hasActiveCoverage: boolean
  hasUpcomingPlannedAppointment?: boolean
}

export const PatientNavbar = ({ hasActiveCoverage, hasUpcomingPlannedAppointment }: PatientNavbarProps) => {
  const { search } = useLocation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const isChatLocation = useIsChatLocation()

  const threadSelected = useOptionalChatThreadContext((store) => store.selectedThread)

  const getTabRoute = (subRoute?: string) => {
    if (isChatLocation) {
      return getChatRoute(threadSelected?.id, subRoute)
    }
    return `/patients/${countryCode}/${patientId}${subRoute ? `/${subRoute}` : ""}${search}`
  }

  const TabItems = [
    {
      id: "history-tab",
      title: "History",
      icon: <ClockCounterClockwise />,
      link: "history",
    },
  ]

  return (
    <div
      className="border-border bg-surface flex flex-col content-center justify-between border-r pt-20 pb-2"
      data-testid={"patient-navbar"}>
      <div className="flex flex-col items-center gap-2">
        <Link to={getTabRoute()} data-testid={"patient-problems-link"}>
          <TabButton
            id="problems-tab"
            title="Problems"
            icon={<List />}
            isActive={
              !location.pathname.includes(PATIENT_ROUTE.INFORMATION) &&
              !location.pathname.includes(PATIENT_ROUTE.AGENDA) &&
              !location.pathname.includes(PATIENT_ROUTE.HISTORY)
            }
          />
        </Link>
        <Link to={getTabRoute(PATIENT_ROUTE.AGENDA)} data-testid={"patient-agenda-link"}>
          <TabButton
            id="agenda-tab"
            title="Agenda"
            icon={hasUpcomingPlannedAppointment ? <CalendarBlankWarningIcon /> : <CalendarBlank />}
            isActive={location.pathname.includes(PATIENT_ROUTE.AGENDA)}
          />
        </Link>
        <Link to={getTabRoute(PATIENT_ROUTE.INFORMATION)} data-testid={"patient-information-link"}>
          <TabButton
            id="information-tab"
            title="Information"
            icon={hasActiveCoverage ? <UserGear /> : <UserWarningIcon />}
            isActive={location.pathname.includes(PATIENT_ROUTE.INFORMATION)}
          />
        </Link>
      </div>
      <div className="flex flex-col items-center gap-2">
        {TabItems.map(({ id, title, icon, link }) => (
          <Link key={id} to={link + search}>
            <TabButton id={id} title={title} icon={icon} isActive={location.pathname.includes(link)} />
          </Link>
        ))}
        <div className="flex flex-col items-center">
          <UserPopup />
          <ReleaseInfo />
        </div>
      </div>
    </div>
  )
}
